import {
  ImageReference,
  PanelInfo,
  WellKnownPose,
  wellKnownPoses,
  InteractivePersona,
  getPrimaryPanelKey,
  isApiManagedPersona,
} from ".";

/**
 * Information needed to render a persona image.
 * This can be passed directly to PersonaImage component.
 */
export interface PersonaImageInfo {
  imageRef: ImageReference;
  variationId?: string; // Panel name (for panels) or variation key (for full variations) or "default"/"source"
  panel?: PanelInfo; // Panel information if this is a panel image
  /**
   * The final size multiplier for this image: persona x sheet x panel, already
   * multiplied out. 1 means "as drawn".
   *
   * Resolved here rather than at the renderer because only this side knows all
   * three: the persona owns the first factor, and a component handed a single
   * `PersonaImageInfo` has no way back to the character it came from.
   */
  scale?: number;
}

/**
 * Multiply the three relative-size factors into one.
 *
 * Each is independent and each defaults to 1, so any that has not been set
 * simply drops out. They compose because they describe different things: how
 * big the CHARACTER is against the rest of the cast, how big that character is
 * drawn on THIS SHEET, and how big THIS POSE sits inside its own frame.
 */
export function relativeScaleOf(
  persona?: { relativeScale?: number } | null,
  image?: { relativeScale?: number } | null,
  panel?: { relativeScale?: number } | null
): number {
  return (
    (persona?.relativeScale ?? 1) *
    (image?.relativeScale ?? 1) *
    (panel?.relativeScale ?? 1)
  );
}

/**
 * Build a PersonaImageInfo from a paneled ImageReference (an expression sheet)
 * and a panel key (e.g. "A16"). Returns null if the panel is not present on
 * the reference. Useful when an Activity / payload carries `image` (the sheet
 * ref) plus `imagePanelKey` and the renderer needs to crop a single panel.
 */
export function personaImageInfoFromRef(
  image: ImageReference | undefined,
  panelKey: string | undefined
): PersonaImageInfo | null {
  if (!image) return null;
  if (!panelKey) {
    // No persona in scope here, so only the sheet factor is known. Callers that
    // have the character should prefer PersonaImageCollection, which resolves
    // all three.
    return {
      imageRef: image,
      variationId: "default",
      scale: relativeScaleOf(null, image, null),
    };
  }
  const panelDetails = image.panels?.details?.[panelKey];
  if (!panelDetails || panelDetails.disabled) {
    return null;
  }
  return {
    imageRef: image,
    variationId: panelKey,
    scale: relativeScaleOf(null, image, panelDetails),
    panel: {
      name: panelKey,
      ...panelDetails,
      width: image.panels!.width,
      height: image.panels!.height,
      columns: image.panels!.columns,
      rows: image.panels!.rows,
      baselineSessionId: image.panels!.baselineSessionId,
    },
  };
}

/**
 * Manages and abstracts persona image collections.
 *
 * Flattens all images from a persona (source, default, variations, panels)
 *
 * Collisions in panel names are allowed and the first match will be used.
 */
export class PersonaImageCollection {
  private entries: PersonaImageInfo[] = [];
  private posesMap: Partial<Record<WellKnownPose, PersonaImageInfo[]>> = {};
  private persona: InteractivePersona | null;

  constructor(persona: InteractivePersona | null) {
    this.persona = persona;
    this.buildEntries();
  }

  getPoses(pose: WellKnownPose): PersonaImageInfo[] {
    return this.posesMap[pose] ?? [];
  }

  /**
   * Builds the flattened list of all available images.
   */
  private buildEntries(): void {
    this.entries = [];
    this.posesMap = Object.fromEntries(wellKnownPoses.map((p) => [p, []])) as Partial<
      Record<WellKnownPose, PersonaImageInfo[]>
    >;

    if (!this.persona) {
      return;
    }

    // Add default image
    if (this.persona.image) {
      this.entries.push({
        imageRef: this.persona.image,
        variationId: "default",
        scale: relativeScaleOf(this.persona, this.persona.image, null),
      });
    }

    // Add image variations and their panels
    if (this.persona.imageVariations) {
      Object.entries(this.persona.imageVariations)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([variationKey, imageRef]) => {
          // Add panel images if this variation has panels
          if (imageRef.panels && imageRef.panels.details) {
            for (const panelDetailKey of Object.keys(imageRef.panels.details).sort(comparePanelOrVariationId)) {
              const panelDetails = imageRef.panels!.details[panelDetailKey];
              const panelConfig = {
                width: imageRef.panels.width,
                height: imageRef.panels.height,
                columns: imageRef.panels.columns,
                rows: imageRef.panels.rows,
                baselineSessionId: imageRef.panels.baselineSessionId,
              };
              if (!panelDetails.disabled) {
                // Use only the panel name as the variationId
                this.entries.push({
                  imageRef: imageRef,
                  variationId: panelDetailKey,
                  scale: relativeScaleOf(this.persona, imageRef, panelDetails),
                  panel: {
                    name: panelDetailKey,
                    ...panelDetails,
                    ...panelConfig,
                  },
                });
              }
            }
          } else {
            // Add the full variation image (use variation key as the variationId)
            this.entries.push({
              imageRef: imageRef,
              variationId: variationKey,
              scale: relativeScaleOf(this.persona, imageRef, null),
            });
          }
        });
    }

    // Populate poses from entries that have panel.pose
    for (const entry of this.entries) {
      const pose = entry.panel?.pose;
      if (pose && entry.variationId) {
        const arr = this.posesMap[pose];
        const alreadyHas = arr?.some((e) => e.variationId === entry.variationId);
        if (!alreadyHas) {
          if (arr) {
            arr.push(entry);
          } else {
            this.posesMap[pose] = [entry];
          }
        }
      }
    }
  }

  /**
   * Gets all available image entries.
   * Useful for displaying lists or selectors.
   * Returns entries with variationId.
   */
  getAllEntries(): Array<{
    variationId: string; // Panel name for panels, variation key for full variations
    imageRef: ImageReference;
    panel?: PanelInfo; // Panel information if this is a panel image
  }> {
    return this.entries.map((entry) => ({
      imageRef: entry.imageRef,
      variationId: entry.variationId!,
      panel: entry.panel,
    }));
  }

  /**
   * Looks up an image by variationId (panel name, variation key, "default", or "source").
   * Returns PersonaImageInfo that can be passed directly to PersonaImage.
   *
   * Falls back to default image if not found.
   *
   * @param variationId - Panel name, variation key, "default", or "source"
   * @returns PersonaImageInfo or null if no images available
   */
  lookup(variationId: string | null | undefined): PersonaImageInfo | null {
    if (!variationId) {
      return this.getDefault();
    }

    // Find the first entry matching the variationId
    const entry = this.entries.find((e) => e.variationId === variationId);

    if (entry) {
      return entry;
    }

    // Fallback to default
    return this.getDefault();
  }

  /**
   * Gets the default image: A1, else (version-3 personas, which may have no A sheet) the first panel of the first
   * available sheet, else the reference image ("default"), else the source, else anything.
   */
  getDefault(): PersonaImageInfo | null {
    const A1Entry = this.entries.find((e) => e.variationId === "A1");
    if (A1Entry) {
      return A1Entry;
    }

    // Version-3 personas may have no A sheet: their first pose stands in for A1. Older personas keep their
    // portrait (a v1 character can carry a news-anchor "N" sheet that must not become its default picture).
    if (isApiManagedPersona(this.persona as { version?: number } | null)) {
      const primaryPanel = getPrimaryPanelKey(this.persona);
      const primaryEntry = primaryPanel && this.entries.find((e) => e.variationId === primaryPanel);
      if (primaryEntry) {
        return primaryEntry;
      }
    }

    // Try default first
    const defaultEntry = this.entries.find((e) => e.variationId === "default");
    if (defaultEntry) {
      return defaultEntry;
    }

    // Fallback to source
    const sourceEntry = this.entries.find((e) => e.variationId === "source");
    if (sourceEntry) {
      return sourceEntry;
    }

    // Fallback to first available entry
    if (this.entries.length > 0) {
      return this.entries[0];
    }

    return null;
  }

  /**
   * Checks if a variationId exists in the collection.
   */
  hasVariationId(variationId: string): boolean {
    return this.entries.some((e) => e.variationId === variationId);
  }

  /**
   * Gets the number of available images.
   */
  get length(): number {
    return this.entries.length;
  }
}

/**
 * Sort panel / variation id strings: A1, A2, A3, A10 (numeric suffix).
 * Case-insensitive letter prefix; multi-letter prefixes (e.g. AA1).
 * Pure-digit keys sort numerically. Fallback: Unicode numeric collation.
 */
export function comparePanelOrVariationId(a: string, b: string): number {
  const letterDigits = /^([A-Za-z]+)(\d+)$/;
  const ma = a.match(letterDigits);
  const mb = b.match(letterDigits);
  if (ma && mb) {
    const pa = ma[1].toUpperCase();
    const pb = mb[1].toUpperCase();
    if (pa !== pb) return pa.localeCompare(pb);
    return Number(ma[2]) - Number(mb[2]);
  }
  const digitsOnly = /^(\d+)$/;
  const da = a.match(digitsOnly);
  const db = b.match(digitsOnly);
  if (da && db) {
    return Number(da[1]) - Number(db[1]);
  }
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}
